import { useUserInfo } from '../../hooks/use-user-info';
import { getHtmlCopy, getTextCopy } from '../../i18n';
import { Checkbox } from '../checkbox/Checkbox';
import { Modal } from '../modal/Modal';
import { Icon } from '../icon';
import { useOverdriveTracking } from './use-overdrive-tracking';
import { Message } from '../../components/utils/Message';
import { stripTags } from '../../utilities/text';

import styles from './CookieSettingsModal.module.scss';
import clsx from 'clsx';

export interface CookieSettingsModalProps {
    cookieDomain?: string;
    isDismissable?: boolean;
    isGdprCountry?: boolean;
    onClose?: () => void;
    isOpen?: boolean;
}

export const CookieSettingsModal = ({
    cookieDomain,
    isDismissable = true,
    isGdprCountry,
    onClose,
    isOpen,
}: CookieSettingsModalProps) => {
    const { dyslexic, highContrast, locale } = useUserInfo();
    const [
        updateTracking,
        analyticsTrackingEnabled,
        setAnalyticsTrackingEnabled,
        performanceTrackingEnabled,
        setPerformanceTrackingEnabled,
    ] = useOverdriveTracking(cookieDomain, isGdprCountry);
    const [saveDefaultTracking] = useOverdriveTracking(
        cookieDomain,
        isGdprCountry
    );

    const confirm = () => {
        updateTracking();
        onClose?.();

        // We need to send the cookie to the server before it can take effect
        window.location.reload();
    };

    const implicitConsent = () => {
        // If the cookie exists, this is an idempotent R/W.
        // If the cookie doesn't exist and isGdprCountry is true, the modal isn't dismissable anyway.
        saveDefaultTracking();
        onClose?.();
        // Reload is too jarring for implicit consent. We can set the tracking on the next navigation.
    };

    const _getAccessibilitySettings = () => {
        const accessibilitySettings = [];
        if (highContrast) {
            accessibilitySettings.push('high-contrast=1');
        }
        if (dyslexic) {
            accessibilitySettings.push('dyslexic=1');
        }

        if (accessibilitySettings.length === 0) {
            return '';
        }

        return `?${accessibilitySettings.join('&')}`;
    };

    const _getPrivacyLink = () => {
        if (locale === 'de') {
            return `<a target="_blank" rel="noreferrer" href="https://company.cdn.overdrive.com/policies/datenschutzvereinbarung${_getAccessibilitySettings()}">${getTextCopy(
                'footerPrivacyNotice'
            )}</a>`;
        }
        return `<a target="_blank" rel="noreferrer" href="https://company.cdn.overdrive.com/policies/privacy-policy.htm${_getAccessibilitySettings()}">${getTextCopy(
            'footerPrivacyPolicy'
        )}</a>`;
    };

    return (
        <Modal
            heading={getHtmlCopy('gdpr.settingsPanel.title')}
            id="cookie-settings-modal"
            open={isOpen ?? false}
            onClose={implicitConsent}
            isDismissable={isDismissable}
            slots={{
                root: {
                    classes: styles.cookieSettingsModal,
                },
            }}
        >
            <div className={styles.scrollSection}>
                <Message
                    elementType="div"
                    messageKey="gdpr.settingsPanel.summary"
                />

                <div className={styles.optionContainer}>
                    <Icon name="check" />

                    <Message
                        className={styles.requiredHeading}
                        elementType="h3"
                        messageKey="gdpr.settingsPanel.requiredHeading"
                    ></Message>

                    <Message
                        className={clsx(
                            styles.labelSubtext,
                            styles.requiredSubtext
                        )}
                        elementType="span"
                        messageKey="gdpr.settingsPanel.requiredBody"
                    />
                </div>

                <div className={styles.optionContainer}>
                    <span>
                        <Checkbox
                            labelKey="gdpr.settingsPanel.performanceHeading"
                            onChange={setPerformanceTrackingEnabled}
                            value={performanceTrackingEnabled}
                        />
                    </span>

                    <Message
                        className={styles.labelSubtext}
                        elementType="span"
                        messageKey="gdpr.settingsPanel.performanceBody"
                    />
                </div>

                <div className={styles.optionContainer}>
                    <span>
                        <Checkbox
                            labelKey="gdpr.settingsPanel.analyticsHeading"
                            onChange={setAnalyticsTrackingEnabled}
                            value={analyticsTrackingEnabled}
                            aria-label={
                                stripTags(
                                    getTextCopy(
                                        'gdpr.settingsPanel.analyticsHeading'
                                    )
                                ) +
                                stripTags(
                                    getTextCopy(
                                        'gdpr.settingsPanel.remarketingHeading'
                                    )
                                )
                            }
                            aria-describedby={
                                'allowUserBehaviorCookiesBody remarketingCookiesBody'
                            }
                        />
                    </span>

                    <Message
                        className={styles.labelSubtext}
                        elementType="span"
                        messageKey="gdpr.settingsPanel.analyticsBody"
                        id="allowUserBehaviorCookiesBody"
                    />
                </div>

                <div className={styles.optionContainer}>
                    <span>
                        <Checkbox
                            labelKey="gdpr.settingsPanel.remarketingHeading"
                            onChange={setAnalyticsTrackingEnabled}
                            value={analyticsTrackingEnabled}
                            tabIndex={-1}
                        />
                    </span>

                    <Message
                        className={styles.labelSubtext}
                        elementType="span"
                        messageKey="gdpr.settingsPanel.remarketingBody"
                        id="remarketingCookiesBody"
                    />
                    <Message
                        className={clsx(
                            styles.labelSubtext,
                            styles.remarketingAdditionalInfo
                        )}
                        elementType="span"
                        messageKey="gdpr.settingsPanel.remarketingAdditionalInfo"
                    />
                </div>
            </div>
            <div className={styles.divider} />

            <div
                className={styles.addendum}
                dangerouslySetInnerHTML={{
                    __html: getTextCopy('gdpr.settingsPanel.addendum', {
                        cookiePolicyLink: `<a target="_blank" rel="noreferrer" href="https://company.cdn.overdrive.com/policies/cookie-policy.htm${_getAccessibilitySettings()}">${getTextCopy(
                            'gdpr.cookiePolicy'
                        )}</a>`,
                        privacyPolicyLink: _getPrivacyLink(),
                    }),
                }}
            ></div>

            <div className={styles.actions}>
                <button className="button radius secondary" onClick={confirm}>
                    <Message messageKey="gdpr.settingsPanel.confirm" />
                </button>
            </div>
        </Modal>
    );
};
